import { useEffect } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { AccountProvider, useMyAccounts } from './AccountContext';
import { rememberAccountId } from './last-account';

/** Layout for `/a/:accountId/*` — turns the id in the URL into the account every
 *  screen below reads, and refuses ids that are not the caller's.
 *
 *  The refusal is a courtesy, not the security boundary: `@@allow` rules in
 *  schema.zmodel already deny every row of an account you are not a member of, so a
 *  hand-edited URL would render an empty screen rather than someone else's calls. This
 *  turns that empty screen into the picker.
 *
 *  It also records where you landed, so a cold start of the installed app reopens the
 *  same line. That record is only ever read by the `/` resolver — see
 *  `last-account.ts`. */
export default function AccountScope() {
  const { accountId } = useParams();
  const { accounts, isLoading } = useMyAccounts();
  const current = accounts.find((a) => a.accountId === accountId);

  useEffect(() => {
    if (current) rememberAccountId(current.accountId);
  }, [current]);

  // No decision without an answer — the same reason RoleGate renders nothing while
  // `me` is in flight. Redirecting on a not-yet-loaded list would bounce every
  // deep link through the picker on a cold start.
  if (isLoading) return null;
  if (!current) return <Navigate to="/accounts" replace />;

  return (
    <AccountProvider value={current}>
      <Outlet />
    </AccountProvider>
  );
}
